import React from 'react';

// Company sales goal bar — 2026 cumulative sales toward RM 100M, shown as a
// STAGE bar: each tier is its own 0→100% fill. When a tier is reached the bar
// changes colour and resets to fill toward the next, higher tier.
//
// Stages derived from [0, ...targets, goal]:
//   Stage 1: 0    → 17M
//   Stage 2: 17M  → 31M
//   Stage 3: 31M  → 56M
//   Stage 4: 56M  → 100M 🏆
//
// To retune the goal/milestones or per-stage colours, edit the two constants
// below (one place). STAGE_THEMES[i] styles stage i (cycles if fewer).
//
// Pure presentational: parent fetches `total` (GET /Sales/GetCompanyGoalTotal).
export const SALES_GOAL = {
    year: 2026,
    goal: 100_000_000,
    targets: [17_000_000, 31_000_000, 56_000_000], // exponential ~1.8x
};

// Per-stage look: a vivid multi-stop gradient (animated as a flowing sheen) plus
// a glow colour for the current-stage halo. Tweak freely; index = stage − 1.
export const STAGE_THEMES = [
    { grad: 'linear-gradient(90deg,#22D3EE 0%,#3B82F6 50%,#22D3EE 100%)', glow: '#3B82F6' }, // Stage 1 · cyan→blue
    { grad: 'linear-gradient(90deg,#6366F1 0%,#8B5CF6 50%,#6366F1 100%)', glow: '#8B5CF6' }, // Stage 2 · indigo→violet
    { grad: 'linear-gradient(90deg,#10B981 0%,#84CC16 50%,#10B981 100%)', glow: '#10B981' }, // Stage 3 · emerald→lime
    { grad: 'linear-gradient(90deg,#F59E0B 0%,#FBBF24 50%,#F97316 100%)', glow: '#F59E0B' }, // Stage 4 · amber→gold
];

// Compact RM formatter: 3_302_581 -> "RM 3.3M", 950_000 -> "RM 950K".
const fmtCompact = (val) => {
    const v = Number(val) || 0;
    if (v >= 1_000_000) return `RM ${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
    if (v >= 1_000) return `RM ${Math.round(v / 1_000)}K`;
    return `RM ${v.toLocaleString('en-MY')}`;
};

const fmtFull = (val) =>
    `RM ${(Number(val) || 0).toLocaleString('en-MY', { maximumFractionDigits: 0 })}`;

const CompanyGoalBar = ({ total = 0, goal = SALES_GOAL.goal, targets = SALES_GOAL.targets }) => {
    const t = Number(total) || 0;
    const safeGoal = Number(goal) > 0 ? Number(goal) : 1;

    // Build ascending stage boundaries: [0, ...targets≤goal, goal].
    const bounds = [0, ...(targets || [])
        .map(Number)
        .filter(x => x > 0 && x < safeGoal)
        .sort((a, b) => a - b), safeGoal];
    const stageCount = bounds.length - 1;

    // Which stage are we in? The first stage whose upper bound exceeds total.
    // Once total ≥ goal, pin to the final stage at 100%.
    let stageIdx = bounds.findIndex((b, i) => i > 0 && t < b) - 1;
    if (stageIdx < 0) stageIdx = stageCount - 1; // reached/exceeded goal
    const reachedGoal = t >= safeGoal;

    const stageStart = bounds[stageIdx];
    const stageEnd = bounds[stageIdx + 1];
    const span = stageEnd - stageStart || 1;
    const stagePct = reachedGoal ? 100 : Math.min(100, Math.max(0, ((t - stageStart) / span) * 100));
    const overallPct = Math.min(100, (t / safeGoal) * 100);
    const theme = STAGE_THEMES[stageIdx % STAGE_THEMES.length];

    return (
        <div style={{ background: 'white', borderRadius: 12, padding: '18px 20px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: 20 }}>
            {/* Title + figure row */}
            <div className="cgb-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: 16 }}>🌊</span>
                    <div style={{
                        fontSize: 15, fontWeight: 800, letterSpacing: '0.02em',
                        background: 'linear-gradient(90deg,#0EA5E9,#2563EB,#0EA5E9)',
                        backgroundSize: '200% 100%', WebkitBackgroundClip: 'text', backgroundClip: 'text',
                        WebkitTextFillColor: 'transparent', animation: 'cgbFlow 3s linear infinite',
                    }}>
                        Towards No.1 by {SALES_GOAL.year}
                    </div>
                </div>
                <div style={{ fontSize: 13, color: '#64748B' }} title={`${fmtFull(t)} of ${fmtFull(safeGoal)}`}>
                    <span style={{ color: '#10B981', fontWeight: 700, fontSize: 15 }}>{fmtCompact(t)}</span>
                    {' '} / {fmtCompact(safeGoal)} · {overallPct.toFixed(1)}%
                    {reachedGoal && <span style={{ marginLeft: 8 }}>🎉 Goal reached!</span>}
                </div>
            </div>

            {/* Stage label */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, margin: '8px 0 8px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>
                    {reachedGoal ? `Final stage complete` : `Stage ${stageIdx + 1} of ${stageCount}`}
                </div>
                <div style={{ fontSize: 12, color: '#94A3B8' }}>
                    {fmtCompact(stageStart)} → <span style={{ fontWeight: 700, color: '#475569' }}>{fmtCompact(stageEnd)}</span>
                    {' '}· {stagePct.toFixed(0)}%
                </div>
            </div>

            {/* Stage fill bar — this stage's 0→100% only. Animated flowing gradient
                + glow halo + a periodic shine sweep so the bar feels alive. */}
            <div style={{ position: 'relative', height: 20, borderRadius: 999, background: '#EEF2F7', overflow: 'hidden', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)' }}>
                <div style={{
                    position: 'relative',
                    width: `${stagePct}%`, height: '100%', borderRadius: 999, overflow: 'hidden',
                    background: theme.grad,
                    backgroundSize: '200% 100%',
                    animation: 'cgbFlow 3s linear infinite',
                    boxShadow: `0 0 10px ${theme.glow}99, 0 0 4px ${theme.glow}`,
                    transition: 'width 1s cubic-bezier(0.22,1,0.36,1)',
                }}>
                    {/* shine sweep */}
                    <div style={{
                        position: 'absolute', top: 0, left: 0, height: '100%', width: '40%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)',
                        animation: 'cgbShine 2.6s ease-in-out infinite',
                    }} />
                </div>
            </div>

            {/* Step dots: one per stage. Done = solid green, current = ring, future = grey */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12 }}>
                {Array.from({ length: stageCount }).map((_, i) => {
                    const done = i < stageIdx || reachedGoal;
                    const current = i === stageIdx && !reachedGoal;
                    const upper = bounds[i + 1];
                    return (
                        <React.Fragment key={i}>
                            <div title={`Stage ${i + 1}: up to ${fmtCompact(upper)}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div style={{
                                    width: 12, height: 12, borderRadius: '50%',
                                    background: done ? '#10B981' : current ? '#fff' : '#E2E8F0',
                                    border: current ? `3px solid ${theme.glow}` : done ? 'none' : '1px solid #CBD5E1',
                                    boxSizing: 'border-box',
                                    animation: current ? 'cgbPulse 1.6s ease-in-out infinite' : 'none',
                                }} />
                                <span style={{ fontSize: 10, fontWeight: 600, color: done ? '#10B981' : current ? '#1A8CFF' : '#94A3B8' }}>
                                    {done ? '✓ ' : ''}{fmtCompact(upper)}
                                </span>
                            </div>
                            {i < stageCount - 1 && (
                                <div style={{ flex: 1, height: 2, background: (i < stageIdx || reachedGoal) ? '#10B981' : '#E2E8F0', borderRadius: 2 }} />
                            )}
                        </React.Fragment>
                    );
                })}
                <span style={{ marginLeft: 4, fontSize: 12 }}>🏆</span>
            </div>

            <style>{`
                @keyframes cgbFlow { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }
                @keyframes cgbShine { 0% { transform: translateX(-120%); } 60%, 100% { transform: translateX(360%); } }
                @keyframes cgbPulse {
                    0%, 100% { box-shadow: 0 0 0 0 ${theme.glow}66; }
                    50% { box-shadow: 0 0 0 5px ${theme.glow}00; }
                }
                @media (max-width: 600px) {
                    .cgb-head { flex-direction: column; align-items: flex-start; gap: 2px; }
                }
            `}</style>
        </div>
    );
};

export default CompanyGoalBar;
